import { useEffect, useRef } from "react"

import { useTracker } from "src/hooks/use_tracker"

import {
  ADDON_OPTIONS_SHOWN_EVENT,
  SURFACE_PROPERTY,
  addonOptionsShown,
} from "./launchAddOnTracking"

// The denominator for the launch add-on rate: how many launch-eligible items
// were on the list when the form was opened.
//
// It cannot ride on new_launch_started, which is where the measurement plan put
// it. That event fires on both surfaces before the form mounts and before the
// catalog request goes out — on the boater side before a reservation, and so a
// marina, has even been chosen. Moving it there would have changed the meaning
// of an event that already has a trend behind it, so this is its own event.
//
// Fires once per scope, on the render that has an answer. `ready` matters: a
// count of zero is the meaningful case — it says the marina offered nothing,
// which is what separates a low attach rate from a marina that configured
// nothing — and it must not be confused with a catalog still in flight.
export const useAddonOptionsShown = ({
  surface,
  trackingProps,
  catalog,
  ready,
  scopeKey,
}) => {
  const tracker = useTracker()
  const reportedScope = useRef(null)

  useEffect(() => {
    // Both modals stay mounted and toggle `isOpen`, so closing the form is what
    // re-arms this — otherwise it would report once per page load and every
    // later open would be invisible.
    if (!ready || !scopeKey) {
      reportedScope.current = null
      return
    }

    if (reportedScope.current === scopeKey) return

    reportedScope.current = scopeKey

    tracker.trackEvent(ADDON_OPTIONS_SHOWN_EVENT[surface], {
      ...trackingProps,
      surface: SURFACE_PROPERTY[surface],
      addon_options_shown: addonOptionsShown(catalog),
    })
    // trackingProps and catalog are rebuilt every render; the scope key is what
    // decides whether this is a new form to report on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, scopeKey, surface])
}

export default useAddonOptionsShown
